import { useAuthStore } from '@/stores/auth.store'

/**
 * Hook dédié aux vérifications de permissions — plus lisible que d'appeler
 * useAuthStore directement dans chaque composant.
 *
 * @example
 * const { can, is } = usePermission()
 * if (can('accounting:validate')) ...
 * if (is('SUPER_ADMIN')) ...
 */
export function usePermission() {
  const { user, hasPermission, hasRole, hasAnyPermission, hasAllPermissions } = useAuthStore()

  return {
    /** Vérifie une permission — SUPER_ADMIN bypass automatique */
    can: hasPermission,
    /** Vérifie plusieurs permissions (toutes requises) */
    canAll: hasAllPermissions,
    /** Vérifie si au moins une permission est présente */
    canAny: hasAnyPermission,
    /** Vérifie un rôle exact */
    is: hasRole,
    /** Vérifie si l'utilisateur est SUPER_ADMIN */
    isSuperAdmin: user?.roles.includes('SUPER_ADMIN') ?? false,
    /** Vérifie si l'utilisateur est ADMIN ou SUPER_ADMIN */
    isAdmin: (user?.roles.includes('ADMIN') || user?.roles.includes('SUPER_ADMIN')) ?? false,
  }
}

/**
 * Version hook unique pour un seul check — pratique dans les conditions JSX
 * @example
 * const canValidate = useCanDo('accounting:validate')
 */
export function useCanDo(permission: string): boolean {
  const { hasPermission } = useAuthStore()
  return hasPermission(permission)
}

/**
 * Retourne uniquement les rôles de l'utilisateur courant
 */
export function useUserRoles(): string[] {
  const { user } = useAuthStore()
  return user?.roles ?? []
}
